import { IDateRange } from "../contracts";
import {
  defaultOrdersFilters,
  IPillFilter,
  IPropertyFilter,
  IPurchaseOrderItemsCommonFilters,
  useStore
} from "../store";

export interface IExternalItemsRedirectionFilters {
  orderCreationRange?: IDateRange;
  requestedDeliveryRange?: IDateRange;
  confirmedDeliveryRange?: IDateRange;
  calculatedConfirmRange?: IDateRange;
  status?: number;
  meetRequested?: IPillFilter[];
  opCos?: string[];
  onTime?: IPillFilter[];
  property?: IPropertyFilter;
  purchaseOrderNumber?: string[];
  salesOrderLine?: string[];
  siteName?: string[];
  siteId?: string[];
}

export const usePOItemsFiltersActions = () => {
  const [_, setState] = useStore();

  const setOrdersCreationRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, orderCreationRange: range }
    }));
  };

  const setOrdersRequestedDeliveryRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, requestedDeliveryRange: range }
    }));
  };

  const setOrdersConfirmedDeliveryRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, confirmedDeliveryRange: range }
    }));
  };

  const setOrdersPlannedDeliveryRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, plannedDeliveryRange: range }
    }));
  };

  const setOrdersCalculatedConfirmRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, calculatedConfirmRange: range }
    }));
  };

  const setOrdersStatus = (status: number) => {
    const newStatus = (old: number[], status: number) => {
      if (old.includes(status)) {
        return old.filter(s => s !== status);
      } else {
        return [...old, status];
      }
    };

    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        statuses: newStatus(old.poItemsCommonFilters.statuses || [], status)
      }
    }));
  };

  const setOrdersMeetRequested = (filter: IPillFilter[]) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, meetRequested: filter }
    }));
  };

  const setOrdersOnTime = (filter: IPillFilter[]) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: { ...old.poItemsCommonFilters, onTime: filter }
    }));
  };

  const setOrdersOpCo = (opCos: string[]) => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        opCos: opCos.length ? opCos : undefined
      }
    }));
  };

  const setPoItemsServerFilter = (filter: IPurchaseOrderItemsCommonFilters) => {
    setState(old => ({
      ...old,
      poItemsServerFilter: filter
    }));
  };

  /**
   * Sets pre-filled values in individual column filters in the Purchase Order Items list view.
   */
  const setItemsRedirectionFilters = (filters: IExternalItemsRedirectionFilters) => {
    const {
      orderCreationRange,
      requestedDeliveryRange,
      confirmedDeliveryRange,
      status,
      meetRequested,
      opCos,
      onTime,
      purchaseOrderNumber,
      salesOrderLine,
      siteName,
      siteId
    } = filters;

    setState(old => {
      const serverFilterSettings = [
        {
          columnKey: "PurchaseOrderNumber",
          filterValues: purchaseOrderNumber ? purchaseOrderNumber : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SalesOrderLine",
          filterValues: salesOrderLine ? salesOrderLine : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SiteName",
          filterValues: siteName ? siteName : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SiteId",
          filterValues: siteId ? siteId : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        }
      ].filter(x => x.filterValues.length > 0);

      const serverFilterModel = {
        purchaseOrderNumber: {
          filterType: "set",
          values: purchaseOrderNumber
        },
        salesOrderLine: {
          filterType: "set",
          values: salesOrderLine
        },
        siteName: {
          filterType: "set",
          values: siteName
        },
        siteId: {
          filterType: "set",
          values: siteId
        }
      };

      const filteredModel = Object.fromEntries(
        Object.entries(serverFilterModel).filter(
          ([key, value]) => value.values && value.values.length > 0
        )
      );

      return {
        ...old,
        poItemsCommonFilters: {
          ...old.poItemsCommonFilters,
          orderCreationRange: orderCreationRange,
          requestedDeliveryRange,
          confirmedDeliveryRange,
          statuses: status === undefined ? undefined : [status],
          meetRequested: meetRequested || defaultOrdersFilters.meetRequested,
          onTime: onTime || defaultOrdersFilters.onTime,
          opCos,
          purchaseOrderNumbers: purchaseOrderNumber
        },
        serverSideFilterSettings: serverFilterSettings,
        serverSideFilterModel: filteredModel
      };
    });
  };

  const clearOrdersRequestedDeliveryRange = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        requestedDeliveryRange: undefined
      }
    }));
  };

  const clearOrdersConfirmedDeliveryRange = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        confirmedDeliveryRange: undefined
      }
    }));
  };

  const clearOrdersPlannedDeliveryRange = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        plannedDeliveryRange: undefined
      }
    }));
  };

  const clearOrdersCalculatedConfirmRange = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        calculatedConfirmRange: undefined
      }
    }));
  };

  const clearOrdersStatus = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        statuses: undefined
      }
    }));
  };

  const clearOrdersMeetRequested = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        meetRequested: defaultOrdersFilters.meetRequested
      }
    }));
  };

  const clearOrdersOnTime = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        onTime: defaultOrdersFilters.onTime
      }
    }));
  };

  const clearOrdersOpCo = () => {
    setState(old => ({
      ...old,
      poItemsCommonFilters: {
        ...old.poItemsCommonFilters,
        opCos: undefined
      }
    }));
  };

  return {
    setOrdersCreationRange,
    setOrdersRequestedDeliveryRange,
    setOrdersConfirmedDeliveryRange,
    setOrdersPlannedDeliveryRange,
    setOrdersCalculatedConfirmRange,
    setOrdersStatus,
    setOrdersMeetRequested,
    setOrdersOnTime,
    setOrdersOpCo,
    setPoItemsServerFilter,
    setItemsRedirectionFilters,
    clearOrdersRequestedDeliveryRange,
    clearOrdersConfirmedDeliveryRange,
    clearOrdersPlannedDeliveryRange,
    clearOrdersCalculatedConfirmRange,
    clearOrdersStatus,
    clearOrdersMeetRequested,
    clearOrdersOnTime,
    clearOrdersOpCo
  };
};
